import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import memoizeOne from 'memoize-one';
import { Box } from '@mui/material';
import { get_z3k_object_state, HeatingCircuit, IntervalTimetable, Pump, ThreewayTap, TimeInterval, } from 'z3k-config';
import { parseFloatWithComma } from 'utils';
import { unpack_validation, with_root_as_2nd_arg, } from 'webui/Z3KConfigParser/validation/types';
import { all_merge, validate_array, validate_by_specification, validate_object_entries, } from 'webui/Z3KConfigParser/validation/collections';
import { minMax, notNullAnd, undefinedOr } from 'webui/Z3KConfigParser/validation/basic';
import { transformTimeValue } from 'console/shared/components/fields';
import { DHWControlType } from '../utils/dhw-control-type';
import { getPumpId, getSensorId, getTapId } from '../utils/getters';
import { dhw_boiler_pump_id, heating_id_2_external_thermostat_id } from '../constants';
import { CurrentState } from './circuit/current-state';
import { ScheduleCard } from './circuit/schedule-card';
import { circuit_target_minmax, TargetTemperatureSettings, } from './circuit/temperature-settings';
import { WeatherCard } from './circuit/weather-card';
import { ExtendedSettings } from './circuit/extended-settings';
import { BoilerControlType, HeatingControlType } from './type';
import { ThreewayTapSettings } from './circuit/threeway-tap-settings';
import { CircuitIcon } from './circuit/icon';
import { PumpSettings } from 'console/climatic/setting-tabs/circuit/pump-settings';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
const makeOnChangeTabState = memoizeOne((tab_key, onChangeState) => (setter) => onChangeState(tab_key, setter));
const volatile_data_subset = memoizeOne((tab_state, volatile_data, circuit_id) => {
    const hc_state = get_z3k_object_state(volatile_data.z3k_state, circuit_id);
    const circ_pump_id = tab_state.circulation_pump_id;
    const heating_id_2_water_sensor = getSensorId(tab_state?.object_arrays, tab_state.analog_temperature_sensors?.map((sens) => sens.id), circuit_id);
    const target_sensor_id = heating_id_2_water_sensor ?? hc_state?.target_sensor_id;
    return {
        service_mode: volatile_data.service_mode,
        hc_state,
        target_sensor_state: target_sensor_id != null
            ? get_z3k_object_state(volatile_data.z3k_state, target_sensor_id)
            : undefined,
        circulation_pump_state: circ_pump_id != null
            ? get_z3k_object_state(volatile_data.z3k_state, circ_pump_id)
            : undefined,
        boiler_pump_state: get_z3k_object_state(volatile_data.z3k_state, dhw_boiler_pump_id),
        threeway_tap_state: tab_state.threeway_tap != null
            ? get_z3k_object_state(volatile_data.z3k_state, tab_state.threeway_tap.id)
            : undefined,
    };
});
const getActiveBoilersId = (circuit_state) => {
    const result = [];
    circuit_state.configuration.boilers.map((boiler) => {
        if (boiler.control_type === BoilerControlType.RELAY ||
            boiler.control_type === BoilerControlType.DIGITAL) {
            result.push(boiler.id);
        }
    });
    return result;
};
const CircuitPage = ({ tab_key, state, validation, volatile_data, onChangeState, }) => {
    const onChangeTabState = useMemo(() => makeOnChangeTabState(tab_key, onChangeState), [makeOnChangeTabState, tab_key, onChangeState]);
    const this_tab_state = useMemo(() => state[tab_key], [state, tab_key]);
    const this_tab_validation = useMemo(() => unpack_validation(validation, tab_key), [unpack_validation, validation, tab_key]);
    const dhw_selected = useMemo(() => state.configuration.dhw.find((setting) => setting.id == this_tab_state.circuit.id), [state, this_tab_state]);
    const dhw_control_type = useMemo(() => dhw_selected?.control_type, [dhw_selected]);
    const circuit_configuration = useMemo(() => state.configuration.circuits.find((circ) => circ.id == this_tab_state.circuit.id), [state, this_tab_state]);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", sx: { gridRowGap: '1em' }, children: [_jsx(CurrentState, { circuit: this_tab_state.circuit, circuit_control_type: circuit_configuration?.control_type, ...volatile_data_subset(this_tab_state, volatile_data, this_tab_state.circuit.id) }), _jsx(CircuitIcon, { state: this_tab_state, onChangeTabState: onChangeTabState }), volatile_data.service_mode && (_jsx(ExtendedSettings, { state: this_tab_state, dhw_control_type: dhw_control_type, circuit_configuration: circuit_configuration, validation: this_tab_validation, onChangeTabState: onChangeTabState })), _jsx(WeatherCard, { state: this_tab_state, dhw_control_type: dhw_control_type, validation: this_tab_validation, onChangeTabState: onChangeTabState, service_mode: volatile_data.service_mode }), _jsx(TargetTemperatureSettings, { state: this_tab_state, dhw_control_type: dhw_control_type, validation: this_tab_validation, onChangeTabState: onChangeTabState }), _jsx(ScheduleCard, { state: this_tab_state, dhw_control_type: dhw_control_type, validation: this_tab_validation, onChangeTabState: onChangeTabState }), volatile_data.service_mode && (_jsx(PumpSettings, { state: this_tab_state, dhw_control_type: dhw_control_type, circuit_configuration: circuit_configuration, validation: this_tab_validation, onChangeTabState: onChangeTabState })), volatile_data.service_mode &&
                this_tab_state.threeway_tap != undefined &&
                circuit_configuration?.control_type !== HeatingControlType.DIRECT && (_jsx(ThreewayTapSettings, { state: this_tab_state, dhw_control_type: dhw_control_type, validation: this_tab_validation, onChangeTabState: onChangeTabState }))] }));
};
export const find_interval_schedule = (z3k_config, circuit_id) => {
    const timetables = IntervalTimetable.get.all(z3k_config);
    const timetable_ids = timetables.map((tt) => tt.id);
    const id = HeatingMode.get
        .all(z3k_config)
        .map((mode) => mode.heating_zones.find((zone) => zone.heating_circuit === circuit_id)
        ?.temperature_setting)
        .find((temperature_setting) => timetable_ids.includes(temperature_setting));
    return timetables.find((tt) => tt.id === id);
};
const validate_time_string = (time) => {
    const time_string = transformTimeValue(time);
    const match = time_string.match(/^(\d\d):(\d\d)$/);
    if (!match) {
        return 'Некорректное время (объект)';
    }
    const [h, m] = [parseInt(match[1], 10), parseInt(match[2], 10)];
    if (h < 0 || h > 23 || m < 0 || m > 59) {
        return 'Некорректное время';
    }
    return null;
};
export const circuit_tab_for = (for_circuit) => {
    const tab_key = `circuit-${for_circuit.id}`;
    return {
        key: tab_key,
        title: for_circuit.name,
        element: CircuitPage,
        isShown: (state, _volatile_data) => {
            const circuit = state[tab_key]?.circuit; // Без ? падало при добавлении CLIMATIC от Юры
            if (circuit === undefined) {
                return false;
            }
            switch (circuit.type) {
                case HeatingCircuit.Type.HEATING:
                    return state.configuration.circuits.some((settings) => settings.id == circuit.id &&
                        settings.control_type != HeatingControlType.OFF &&
                        !state.configuration.blackList.includes(settings.id));
                case HeatingCircuit.Type.DHW:
                    return state.configuration.dhw.some((settings) => settings.id == circuit.id &&
                        settings.control_type != DHWControlType.OFF &&
                        !state.configuration.blackList.includes(settings.id));
            }
            return false;
        },
        state_subset: (all_tabs_state, tab_key) => {
            return {
                configuration: all_tabs_state.configuration,
                [tab_key]: all_tabs_state[tab_key],
            };
        },
        data2state: ({ device }) => {
            const circuit = HeatingCircuit.get.byId(device.z3k_config, for_circuit.id) ??
                for_circuit;
            const { ui_settings } = device;
            const timetable = find_interval_schedule(device.z3k_config, circuit.id);
            let time_intervals = [];
            if (timetable != null)
                time_intervals = TimeInterval.get.idIn(device.z3k_config, timetable.time_intervals);
            const circ_pump_id = getPumpId(device.z3k_config?.object_arrays, device.z3k_config?.pumps, circuit.id);
            const circulation_pump = circ_pump_id != null
                ? Pump.get.byId(device.z3k_config, circ_pump_id)
                : undefined;
            const boiler_pump = Pump.get.byId(device.z3k_config, dhw_boiler_pump_id);
            let dhw_parallel_heating = false;
            if (circuit.type === HeatingCircuit.Type.DHW) {
                dhw_parallel_heating = !HeatingCircuit.get
                    .where(device.z3k_config, (hc) => hc.type === HeatingCircuit.Type.HEATING)
                    .every(HeatingCircuit.isBlockedWhileDHW);
            }
            const threeway_tap_id = getTapId(device.z3k_config?.object_arrays, device.z3k_config?.threeway_taps, circuit.id);
            const threeway_tap = threeway_tap_id != null
                ? ThreewayTap.get.byId(device.z3k_config, threeway_tap_id)
                : undefined;
            return {
                firmware_version: device.firmware_version?.[0],
                circuit: circuit,
                timetable: timetable,
                time_intervals: time_intervals,
                object_arrays: device.z3k_config?.object_arrays ?? [],
                // pumps: device.z3k_config?.pumps ?? [],
                analog_temperature_sensors: device.z3k_config?.analog_temperature_sensors ?? [],
                // Смеситель
                threeway_tap: threeway_tap,
                state_tap: threeway_tap
                    ? {
                        step_time: threeway_tap?.step_time,
                        coefficient: threeway_tap?.coefficient,
                        closing_time: threeway_tap?.closing_time,
                        step_period: threeway_tap?.step_period,
                    }
                    : undefined,
                target_temperatures: HeatingMode.get
                    .all(device.z3k_config)
                    .filter((mode) => mode.heating_zones.some((zone) => zone.heating_circuit === circuit.id))
                    .filter((mode) => HeatingMode.getTargetTemperature(mode, circuit.id) != null)
                    .map((mode) => [
                    mode,
                    HeatingMode.getTargetTemperature(mode, circuit.id),
                ])
                    .filter(([, temp]) => temp != null),
                start_times: Object.fromEntries(time_intervals.map(({ id, sh, sm }) => [id, { hour: sh, min: sm }])),
                end_times: Object.fromEntries(time_intervals.map(({ id, eh, em }) => [id, { hour: eh, min: em }])),
                heating_modes: HeatingMode.get.all(device.z3k_config),
                winter_summer_switch: circuit.winter_summer_switch,
                summer_threshold: circuit.summer_threshold,
                // enable_pza: circuit.pza !== 0,
                pza: circuit.pza != null && [0, 255].includes(circuit.pza)
                    ? 1.2
                    : circuit.pza,
                hysteresis: circuit.hysteresis,
                heat_request: circuit.heat_request,
                setting_register: circuit.setting_register,
                turn_off_delay: Math.round(circuit.turn_off_delay / 60),
                circulation_pump_delay: circulation_pump?.delay ?? null,
                circulation_pump_id: circ_pump_id,
                circulation_pump_mode: circulation_pump != null
                    ? Pump.getMode(circulation_pump)
                    : Pump.Mode.BY_HEAT_REQUEST,
                boiler_pump_delay: boiler_pump?.delay ?? null,
                water_min_temperature: typeof circuit.water_min_temperature === 'string'
                    ? +circuit.water_min_temperature
                    : circuit.water_min_temperature,
                water_max_temperature: typeof circuit.water_max_temperature === 'string'
                    ? +circuit.water_max_temperature
                    : circuit.water_max_temperature,
                control_mode: circuit.type === HeatingCircuit.Type.HEATING
                    ? HeatingCircuit.getControlModeClimatic(circuit)
                    : null,
                dhw_use_circ_pump: circuit.type === HeatingCircuit.Type.DHW &&
                    circ_pump_id != null &&
                    circuit.control_devices.includes(circ_pump_id),
                dhw_parallel_heating,
                antileg_enabled: HeatingCircuit.isAntilegEnabled(circuit),
                antileg_time: {
                    hour: circuit.antileg_mode_options.h,
                    min: circuit.antileg_mode_options.m,
                },
                antileg_days: circuit.antileg_mode_options.days,
                icon: ui_settings?.icons?.heating_circuits?.[circuit.id],
                no_stop_heating_when_pid_is_in_low_threshold: HeatingCircuit.isNoStopHeatingWhenPidIsInLowThreshold(circuit),
            };
        },
        validate: with_root_as_2nd_arg(validate_by_specification({
            [tab_key]: all_merge(validate_by_specification({
                state_tap: undefinedOr(validate_by_specification({
                    step_time: notNullAnd(minMax(0, 10)),
                    closing_time: notNullAnd(minMax(0, 300)),
                    coefficient: notNullAnd(minMax(0, 3)),
                    step_period: notNullAnd(minMax(10, 180)),
                })),
                target_temperatures: validate_array(([, target], root) => {
                    const state = root[tab_key];
                    const { circuit, control_mode, water_min_temperature, water_max_temperature, } = state;
                    const [min, max] = circuit_target_minmax(circuit.type, control_mode, water_min_temperature, water_max_temperature);
                    if (target == null || isNaN(target))
                        return 'Введите температуру';
                    if (target < min || target > max)
                        return `Допустимые значения: от ${min} до ${max} °C`;
                    return null;
                }),
                start_times: validate_object_entries(([, time_str]) => validate_time_string(time_str)),
                end_times: validate_object_entries(([, time_str]) => validate_time_string(time_str)),
                summer_threshold: (value, root) => {
                    const { winter_summer_switch } = root[tab_key];
                    if (!winter_summer_switch)
                        return null;
                    if (value == null || isNaN(value))
                        return 'Введите температуру в °C';
                    return minMax(5, 35)(value);
                },
                pza: (value, root) => {
                    const { circuit } = root[tab_key];
                    // if (!enable_pza) return null;
                    if (circuit.pza === 0xff) {
                        return null;
                    }
                    if (value == null) {
                        return 'Введите номер кривой';
                    }
                    const number = parseFloatWithComma(value);
                    if (number == null || isNaN(number))
                        return 'Введите номер кривой';
                    return minMax(0.2, 4.0)(number);
                },
                hysteresis: notNullAnd(minMax(0.1, 10)),
                turn_off_delay: notNullAnd(minMax(0, 120)),
                circulation_pump_delay: notNullAnd(minMax(0, 120)),
                boiler_pump_delay: (value, root) => {
                    const dhw_selected = root.configuration.dhw.find((setting) => setting.id == root[tab_key].circuit.id);
                    const dhw_control_type = dhw_selected?.control_type;
                    const applies = root[tab_key].circuit.type == HeatingCircuit.Type.DHW &&
                        dhw_control_type == DHWControlType.BOILER;
                    if (applies)
                        return notNullAnd(minMax(0, 120))(value);
                    return null;
                },
                water_min_temperature: (value, root) => {
                    const { circuit } = root[tab_key];
                    if (circuit.type === HeatingCircuit.Type.HEATING) {
                        if (value == null)
                            return 'Введите значение';
                        const number = parseFloatWithComma(value);
                        if (number == null)
                            return 'Введите значение';
                        return minMax(5, 85)(number);
                    }
                    else
                        return null;
                },
                water_max_temperature: (value, root) => {
                    const { circuit } = root[tab_key];
                    if (circuit.type === HeatingCircuit.Type.HEATING) {
                        if (value == null)
                            return 'Введите значение';
                        const number = parseFloatWithComma(value);
                        if (number == null)
                            return 'Введите значение';
                        return minMax(5, 85)(number);
                    }
                    else
                        return null;
                },
                antileg_time: (value, root) => {
                    const { antileg_enabled } = root[tab_key];
                    if (antileg_enabled)
                        return validate_time_string(value);
                    else
                        return null;
                },
            }), (state) => {
                if (state.circuit.type == HeatingCircuit.Type.DHW)
                    return null;
                const water_min_temperature = parseFloatWithComma(state.water_min_temperature?.toString() ?? '') ?? 0;
                const water_max_temperature = parseFloatWithComma(state.water_max_temperature?.toString() ?? '') ?? 0;
                if (water_min_temperature >= water_max_temperature) {
                    return {
                        water_min_temperature: 'Мax. t° теплоносителя должна быть больше Мин. t° теплоносителя',
                        water_max_temperature: 'Мax. t° теплоносителя должна быть больше Мин. t° теплоносителя',
                    };
                }
                return null;
            }),
        })),
        state2data: (state_subset) => (setter) => {
            const state = state_subset[tab_key];
            let z3k_config = setter.z3k_config;
            z3k_config = {
                ...z3k_config,
                heating_circuits: z3k_config.heating_circuits.map((circuit) => {
                    if (circuit.id !== state.circuit.id) {
                        if (state.circuit.type == HeatingCircuit.Type.DHW &&
                            circuit.type == HeatingCircuit.Type.HEATING)
                            return HeatingCircuit.setBlockedWhileDHW(circuit, !state.dhw_parallel_heating);
                        return circuit;
                    }
                    const circuit_settings = state_subset.configuration.dhw.find((settings) => settings.id == circuit.id);
                    const active_boilers = getActiveBoilersId(state_subset);
                    let heat_source = null;
                    if (circuit.type === HeatingCircuit.Type.HEATING ||
                        (circuit.type === HeatingCircuit.Type.DHW &&
                            circuit_settings?.control_type ===
                                DHWControlType.BOILER)) {
                        if (active_boilers.length === 1) {
                            heat_source = active_boilers[0];
                        }
                        if (active_boilers.length >= 2) {
                            if (z3k_config.boiler_cascades?.length) {
                                heat_source = z3k_config.boiler_cascades[0].id;
                            }
                        }
                    }
                    else if (circuit.type === HeatingCircuit.Type.DHW &&
                        circuit_settings?.control_type !== DHWControlType.BOILER) {
                        heat_source = z3k_config.boiler_cascades?.length
                            ? z3k_config.boiler_cascades[0].boiler_list[0]
                            : null;
                    }
                    let new_circuit = {
                        ...circuit,
                        winter_summer_switch: state.winter_summer_switch,
                        hysteresis: parseFloatWithComma(state.hysteresis),
                        heat_request: +state.heat_request,
                        turn_off_delay: parseFloatWithComma(state.turn_off_delay) * 60,
                        heat_source: heat_source,
                    };
                    new_circuit =
                        HeatingCircuit.setNoStopHeatingWhenPidIsInLowThreshold(new_circuit, state.no_stop_heating_when_pid_is_in_low_threshold);
                    if (state.winter_summer_switch)
                        new_circuit.summer_threshold = parseFloatWithComma(state.summer_threshold);
                    if (circuit.type === HeatingCircuit.Type.HEATING) {
                        switch (state.control_mode) {
                            case HeatingCircuit.ControlMode.EXT_THERMOSTAT:
                                new_circuit = HeatingCircuit.setConsumerMode(new_circuit, HeatingCircuit.ConsumerMode.AIR);
                                new_circuit.external_thermostat =
                                    heating_id_2_external_thermostat_id(circuit.id) ?? null;
                                new_circuit.pza = 255;
                                break;
                            case HeatingCircuit.ControlMode.PZA:
                                new_circuit = HeatingCircuit.setConsumerMode(new_circuit, HeatingCircuit.ConsumerMode.WATER);
                                new_circuit.external_thermostat = null;
                                new_circuit.pza =
                                    parseFloatWithComma(state.pza) ?? 0;
                                break;
                            case HeatingCircuit.ControlMode.WATER:
                                new_circuit = HeatingCircuit.setConsumerMode(new_circuit, HeatingCircuit.ConsumerMode.WATER);
                                new_circuit.external_thermostat = null;
                                new_circuit.pza = 255;
                                break;
                            case HeatingCircuit.ControlMode.AIR:
                                new_circuit = HeatingCircuit.setConsumerMode(new_circuit, HeatingCircuit.ConsumerMode.AIR_PID);
                                new_circuit.external_thermostat = null;
                                new_circuit.pza = 255;
                                break;
                            default:
                                throw 'Unknown HeatingCircuit.ControlMode in circuit_tab::state2data';
                        }
                    }
                    if (circuit.type === HeatingCircuit.Type.HEATING) {
                        new_circuit.water_min_temperature = parseFloatWithComma(state.water_min_temperature);
                        new_circuit.water_max_temperature = parseFloatWithComma(state.water_max_temperature);
                    }
                    if (circuit.type == HeatingCircuit.Type.DHW &&
                        circuit_settings.control_type != DHWControlType.OFF) {
                        const circ_pump_id = state.circulation_pump_id;
                        if (state.dhw_use_circ_pump &&
                            !new_circuit.control_devices.includes(circ_pump_id))
                            new_circuit.control_devices = [
                                ...circuit.control_devices,
                                circ_pump_id,
                            ];
                        if (!state.dhw_use_circ_pump &&
                            new_circuit.control_devices.includes(circ_pump_id))
                            new_circuit.control_devices =
                                circuit.control_devices.filter((pump_id) => pump_id != circ_pump_id);
                    }
                    if (circuit.type == HeatingCircuit.Type.DHW) {
                        new_circuit = HeatingCircuit.setAntilegEnabled(new_circuit, state.antileg_enabled);
                        const match = transformTimeValue(state.antileg_time).match(/^(\d\d):(\d\d)$/);
                        new_circuit.antileg_mode_options = {
                            h: parseInt(match[1], 10),
                            m: parseInt(match[2], 10),
                            days: state.antileg_days,
                        };
                    }
                    return new_circuit;
                }),
            };
            const update_target_temperatures = Object.fromEntries(state.target_temperatures.map(([mode, target_temp]) => [
                mode.id,
                HeatingMode.makeTemperatureSetting(parseFloatWithComma(target_temp)),
            ]));
            z3k_config = {
                ...z3k_config,
                heating_modes: z3k_config.heating_modes.map((mode) => {
                    const new_temp = update_target_temperatures[mode.id];
                    if (new_temp == null)
                        return mode;
                    return {
                        ...mode,
                        heating_zones: mode.heating_zones.map((zone) => {
                            if (zone.heating_circuit === state.circuit.id)
                                return { ...zone, temperature_setting: new_temp };
                            return zone;
                        }),
                    };
                }),
            };
            z3k_config = {
                ...z3k_config,
                time_intervals: z3k_config.time_intervals.map((interval) => {
                    let local_interval = state.time_intervals.find((int) => int.id === interval.id);
                    if (local_interval == null)
                        return interval;
                    const new_start_time_str = state.start_times[interval.id];
                    const new_end_time_str = state.end_times[interval.id];
                    if (new_start_time_str != null) {
                        const match = transformTimeValue(new_start_time_str).match(/^(\d\d):(\d\d)$/);
                        local_interval = {
                            ...local_interval,
                            sh: parseInt(match[1], 10),
                            sm: parseInt(match[2], 10),
                        };
                    }
                    if (new_end_time_str != null) {
                        const match = transformTimeValue(new_end_time_str).match(/^(\d\d):(\d\d)$/);
                        local_interval = {
                            ...local_interval,
                            eh: parseInt(match[1], 10),
                            em: parseInt(match[2], 10),
                        };
                    }
                    return local_interval;
                }),
            };
            z3k_config = {
                ...z3k_config,
                interval_timetables: z3k_config.interval_timetables.map((timetable) => {
                    if (timetable.id !== state.timetable?.id) {
                        return timetable;
                    }
                    return state.timetable;
                }),
            };
            const circ_pump_id = state.circulation_pump_id;
            if (state.circuit.type == HeatingCircuit.Type.DHW) {
                z3k_config = Pump.get.modifyById(z3k_config, dhw_boiler_pump_id, (pump) => ({
                    ...Pump.setMode(pump, Pump.Mode.BY_HEAT_REQUEST),
                    delay: parseFloatWithComma(state.boiler_pump_delay),
                }));
                z3k_config = Pump.get.modifyById(z3k_config, circ_pump_id, (pump) => Pump.setMode(pump, state.dhw_use_circ_pump
                    ? Pump.Mode.ALWAYS_WORK
                    : Pump.Mode.BY_HEAT_REQUEST));
            }
            else {
                const circuit_configuration = state_subset.configuration.circuits.find((circ) => circ.id == state.circuit.id);
                const circ_pump_mode = circuit_configuration.control_type == HeatingControlType.DIRECT
                    ? Pump.Mode.BY_HEAT_REQUEST
                    : state.circulation_pump_mode;
                z3k_config = Pump.get.modifyById(z3k_config, state.circulation_pump_id, (pump) => ({
                    ...Pump.setMode(pump, circ_pump_mode),
                    delay: parseFloatWithComma(state.circulation_pump_delay),
                }));
            }
            if (state.threeway_tap !== undefined) {
                z3k_config = ThreewayTap.get.modifyById(z3k_config, state.threeway_tap.id, (tap) => {
                    if (state.threeway_tap !== undefined &&
                        state.state_tap !== undefined)
                        return {
                            ...tap,
                            setting_register: state.threeway_tap.setting_register,
                            closing_time: Number(state.state_tap.closing_time),
                            step_time: Number(state.state_tap.step_time),
                            coefficient: Number(state.state_tap.coefficient),
                            step_period: Number(state.state_tap.step_period),
                        };
                    else
                        return tap;
                });
            }
            const { ui_settings } = setter;
            const { icon, circuit } = state;
            const changed_icon = {
                ...ui_settings,
                icons: {
                    heating_circuits: {
                        ...ui_settings?.icons?.heating_circuits,
                        [circuit.id]: icon,
                    },
                    heating_modes: {
                        ...ui_settings?.icons?.heating_modes,
                    },
                },
            };
            return {
                ...setter,
                z3k_config,
                ui_settings: icon !== undefined ? changed_icon : ui_settings,
            };
        },
    };
};
